import React, { useEffect, useState, cloneElement, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Calendar, MessageCircle, ArrowLeft, Phone, Clock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SERVICES_DATA } from '../data/servicesData';
import { twMerge } from 'tailwind-merge';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const service = SERVICES_DATA.find(s => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-831" data-mp="13u1u9y1vcka7">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-4" data-id="element-832" data-mp="3sh6t3rrpd3m8">
          Service Not Found
        </h1>
        <p className="text-slate-600 mb-8" data-id="element-833" data-mp="18h9gh6kol4bn">
          The service you are looking for does not exist or has been moved.
        </p>
        <Button href="/services" variant="primary" data-id="element-834" data-mp="1df1iitf1i6tk">
          View All Services
        </Button>
      </div>;
  }
  const whatsappMessage = encodeURIComponent(`Hello Maisha Medical Center.\nI would like to inquire about ${service.title}.\nPlease assist me.`);
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-835" data-mp="pq050rrco5hf">
      {/* Hero Band */}
      <div className="bg-brand-navy py-16 relative overflow-hidden" data-id="element-836" data-mp="uh8gdtc8q0x8">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-837" data-mp="uijmhzt5rkri">
          <button onClick={() => navigate('/services')} className="flex items-center gap-2 text-brand-gold hover:text-white transition-colors text-sm font-medium mb-8" data-id="element-838" data-mp="ftx5zursomyp">
            <ArrowLeft className="w-4 h-4" data-id="element-839" data-mp="6s60or162i5d" /> Back to Services
          </button>
          <div className="flex items-center gap-4 mb-6" data-id="element-840" data-mp="bpph9xlc770h">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-brand-gold backdrop-blur-sm" data-id="element-841" data-mp="2dfwlyo54sngc">
              {cloneElement(service.icon as React.ReactElement, {
              className: 'w-8 h-8'
            })}
            </div>
            <div data-id="element-842" data-mp="23qd51o0zb3o4">
              {service.heroHeading && <span className="block text-brand-gold text-sm font-semibold mb-1" data-id="element-843" data-mp="1zpje0ls8p8r0">
                  {service.title}
                </span>}
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white" data-id="element-844" data-mp="2wcelhrq81rn5">
                {service.heroHeading ?? service.title}
              </h1>
            </div>
          </div>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed" data-id="element-845" data-mp="2nfx8sk27llpa">
            {service.intro ?? service.shortDesc}
          </p>
        </div>
      </div>

      <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-846" data-mp="1bzp0s340di68">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-847" data-mp="16zb4fibrrwo1">
          {/* Main Content */}
          <div className="w-full lg:w-2/3 space-y-12" data-id="element-848" data-mp="3infvsrxrz1ue">
            {/* Overview */}
            <section data-id="element-849" data-mp="3nmagy0j7t2ho">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-4" data-id="element-850" data-mp="3nlr5n2o2lccm">
                {service.overviewTitle ?? 'Overview'}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed" data-id="element-851" data-mp="3rlrnkxado5of">
                {service.overview}
              </p>
            </section>

            {/* Sub-services */}
            {service.subServices && service.subServices.length > 0 && <section data-id="element-852" data-mp="a6l3u7iin1v3">
                <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-853" data-mp="a5taz4gazi8i">
                  Our {service.title} Services
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 border-t border-slate-200" data-id="element-854" data-mp="j8korawniq3b">
                  {service.subServices.map(item => <li key={item} className="flex items-center gap-4 py-4 border-b border-slate-200" data-id="element-855" data-mp="2gzndxvl0yt7b">
                      <span className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-brand-gold shrink-0" data-id="element-856" data-mp="1po7n3h13bkk3">
                        {cloneElement(service.icon as React.ReactElement, {
                    className: 'w-5 h-5'
                  })}
                      </span>
                      <span className="font-heading font-semibold text-brand-navy" data-id="element-857" data-mp="1um9847i2tty7">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </section>}

            {/* Benefits */}
            <section className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-858" data-mp="1gpcgw4y1qags">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-859" data-mp="1bx2mablzkm86">
                {service.benefitsTitle ?? 'Key Benefits'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-860" data-mp="j9ghj010vbjq">
                {service.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-861" data-mp="2uvhag77we9x7" data-mpi={idx}>
                    <CheckCircle2 className="w-6 h-6 text-brand-gold shrink-0 mt-0.5" data-id="element-862" data-mp="2zmz4ebbi7a8o" />
                    <span className="text-slate-700 font-medium" data-id="element-863" data-mp="2lvuc5uagtk8d">
                      {benefit}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Process */}
            <section data-id="element-864" data-mp="3do33kjrf7me3">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-8" data-id="element-865" data-mp="3dnjkgzq1aryh">
                What to Expect
              </h2>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-866" data-mp="34mc07lkcrrne">
                {service.process.map((step, idx) => <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-867" data-mp="1y6jewmsbq7yz" data-mpi={idx}>
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-brand-light bg-brand-navy text-white font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-868" data-mp="3uxds2faaxt5x">
                      {idx + 1}
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-869" data-mp="3h5qsa5h5i2ie">
                      <h3 className="font-heading font-bold text-brand-navy text-lg mb-2" data-id="element-870" data-mp="3c87et7sa1tri">
                        {step.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-871" data-mp="336ho6467yyus">
                        {step.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </section>

            {/* FAQs */}
            {service.faqs.length > 0 && <section data-id="element-872" data-mp="2jawu4daw20x7">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-873" data-mp="2j9lsaj10738q">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4" data-id="element-874" data-mp="2t0zpgk5hfqyp">
                {service.faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return <div key={idx} className={twMerge('bg-white border rounded-2xl overflow-hidden transition-all duration-300', isOpen ? 'border-brand-navy shadow-soft' : 'border-slate-200 hover:border-brand-navy/30')} data-id="element-875" data-mp="20cu17nf7zju4" data-mpi={idx}>
                      <button onClick={() => setOpenFaqIndex(isOpen ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-876" data-mp="3kt72zbpoyn8v">
                        <span className="font-heading font-semibold text-lg text-brand-navy pr-8" data-id="element-877" data-mp="31f6potuye00n">
                          {faq.q}
                        </span>
                        <ChevronDown className={twMerge('w-5 h-5 text-brand-red shrink-0 transition-transform duration-300', isOpen ? 'rotate-180' : '')} data-id="element-878" data-mp="3a8a6nc4t4v01" />
                      </button>
                      <div className={twMerge('px-6 overflow-hidden transition-all duration-300 ease-in-out', isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0')} data-id="element-879" data-mp="3f5tiy6hy2xpc">
                        <p className="text-slate-600 leading-relaxed" data-id="element-880" data-mp="1uqzzeecowff0">
                          {faq.a}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </section>}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-1/3 space-y-6" data-id="element-881" data-mp="1avoyul1m46rv">
            {/* Action Card */}
            <div className="bg-white p-8 rounded-2xl shadow-soft border border-slate-100 sticky top-28" data-id="element-882" data-mp="1fvbef14wxz3u">
              <h3 className="font-heading font-bold text-xl text-brand-navy mb-2" data-id="element-883" data-mp="10yas5d5hqwcr">
                {service.ctaTitle ?? 'Ready to get started?'}
              </h3>
              <p className="text-slate-600 text-sm mb-8" data-id="element-884" data-mp="rxvv5gj5zstf">
                Book your {service.title.toLowerCase()} appointment today.
              </p>

              <div className="space-y-4" data-id="element-885" data-mp="wxs13lbf9ubm">
                <Button href={`/book?service=${service.slug}`} variant="primary" fullWidth icon={<Calendar className="w-5 h-5" data-id="element-887" data-mp="2r9ow1o92pibf" />} data-id="element-886" data-mp="wytkqe6i7p4y">
                  {service.ctaLabel ?? 'Book Appointment'}
                </Button>
                <Button href={`https://wa.me/26773241613?text=${whatsappMessage}`} target="_blank" variant="outline" fullWidth icon={<MessageCircle className="w-5 h-5 text-[#25D366]" data-id="element-889" data-mp="m22cnpixvim5" />} className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-brand-navy" data-id="element-888" data-mp="vs543laewoid">
                  {service.whatsappLabel ?? 'Inquire via WhatsApp'}
                </Button>
              </div>

              <hr className="my-8 border-slate-100" data-id="element-890" data-mp="10rhyqs7iyzvi" />

              <div className="space-y-4" data-id="element-891" data-mp="10qyh7clsedi0">
                <div className="flex items-start gap-3" data-id="element-892" data-mp="15q2rk6kg6h9x">
                  <Phone className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-893" data-mp="15ohw8eqb6ub0" />
                  <div data-id="element-894" data-mp="3lgnwtrube3br">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-895" data-mp="3ljjtlcufelym">
                      Call Us Directly
                    </div>
                    <a href="tel:+2676540244" className="text-brand-red font-semibold hover:underline" data-id="element-896" data-mp="3hz0660l0xd4t">
                      +267 6540244
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-897" data-mp="3rvczgxcnpoz1">
                  <Clock className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-898" data-mp="3mzxnkay7m359" />
                  <div data-id="element-899" data-mp="2z8a3abvgdqby">
                    <div className="text-sm font-medium text-brand-navy" data-id="element-900" data-mp="2z9tgftltbl9i">
                      Operating Hours
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-901" data-mp="2u846khabtdn0">
                      Mon - Fri: 8:00 AM - 6:00 PM
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-902" data-mp="347uziej8nkkz">
                      Sat: 9:00 AM - 1:00 PM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}